import { useState, useCallback, useEffect } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const AUTH_KEY = "ayushjha-admin-jwt";
function readToken() { try { return sessionStorage.getItem(AUTH_KEY) || null; } catch { return null; } }
function writeToken(t) { try { sessionStorage.setItem(AUTH_KEY, t); } catch { /* Keep the session in React state. */ } }
function clearToken() { try { sessionStorage.removeItem(AUTH_KEY); } catch { /* No stored session. */ } }

export default function Admin() {
  const [token, setT] = useState(readToken);

  const onLogin = useCallback((jwt) => {
    writeToken(jwt);
    setT(jwt);
  }, []);

  const onLogout = useCallback(() => {
    clearToken();
    setT(null);
  }, []);

  if (!token) return <LoginGate onLogin={onLogin} />;
  return <Dashboard token={token} onLogout={onLogout} />;
}

function LoginGate({ onLogin }) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await api("/admin/login", { body: { password } });
      if (typeof data?.token !== "string" || !data.token) throw new Error("Invalid login response");
      onLogin(data.token);
    } catch {
      setErr("Login failed. Check your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-gate" data-testid="admin-gate">
      <form className="admin-gate__card" onSubmit={submit} data-testid="admin-login-form">
        <div className="admin-gate__icon"><ShieldCheck size={28} strokeWidth={1.4} /></div>
        <span className="overline">◆ Admin · password gate</span>
        <h1 className="serif">Portfolio dashboard</h1>
        <p className="admin-gate__lede">Read-only data — project views, contact briefs, callback requests, engagement.</p>

        <div className="field">
          <label className="overline" htmlFor="admin-password">Password</label>
          <input
            type="password"
            id="admin-password"
            autoComplete="current-password"
            maxLength={1024}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            data-testid="admin-password-input"
            required
            autoFocus
          />
        </div>

        <button type="submit" className="submit-btn" disabled={loading} data-testid="admin-login-submit">
          <span>{loading ? "Verifying…" : "Unlock dashboard"}</span>
        </button>

        {err && <div className="form-status form-status--error" role="alert" data-testid="admin-login-error">{err}</div>}

        <a href="/" className="admin-gate__back" data-testid="admin-gate-back">← Back to site</a>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [views, setViews] = useState({ total: 0, by_project: [] });
  const [contacts, setContacts] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [engages, setEngages] = useState({ by_project: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [v, c, cb, e] = await Promise.all([
          api("/projects/views", { token, signal: controller.signal }),
          api("/contact", { token, signal: controller.signal }),
          api("/callbacks", { token, signal: controller.signal }),
          api("/projects/engagement", { token, signal: controller.signal }),
        ]);
        if (cancelled) return;
        if (!Array.isArray(v?.by_project) || !Array.isArray(c) || !Array.isArray(cb) || !Array.isArray(e?.by_project)) throw new Error("Invalid dashboard response");
        setViews(v);
        setContacts(c);
        setCallbacks(cb);
        setEngages(e);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 401) {
          clearToken();
          onLogout();
        } else {
          setError("Failed to load dashboard data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; controller.abort(); };
  }, [token, onLogout, reload]);

  return (
    <div className="admin" data-testid="admin-root">
      <header className="admin__head">
        <a href="/" className="admin__back" data-testid="admin-back-link">← Back to site</a>
        <div className="admin__head-row">
          <h1 className="serif">Portfolio · Admin</h1>
          <button
            type="button"
            className="admin__logout"
            onClick={onLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} strokeWidth={1.6} />
            <span>Sign out</span>
          </button>
        </div>
        <p className="overline overline--muted">Read-only · live data · session 8h</p>
      </header>

      {loading && <p className="muted" style={{ padding: "2rem" }}>Loading…</p>}
      {error && <div role="alert" className="form-status form-status--error">{error} <button className="chip" onClick={() => setReload(value => value + 1)}>Retry</button></div>}

      {!loading && !error && (
        <>
          <section className="admin__row">
            <div className="admin__kpi" data-testid="kpi-views">
              <div className="overline">Total Project Views</div>
              <div className="serif admin__kpi-val">{views.total}</div>
            </div>
            <div className="admin__kpi" data-testid="kpi-contacts">
              <div className="overline">Recent Contact Briefs (up to 100)</div>
              <div className="serif admin__kpi-val">{contacts.length}</div>
            </div>
            <div className="admin__kpi" data-testid="kpi-callbacks">
              <div className="overline">Recent Callback Requests (up to 200)</div>
              <div className="serif admin__kpi-val">{callbacks.length}</div>
            </div>
          </section>

          <Panel title="Project Views" testid="panel-views">
            <table className="admin-table">
              <thead><tr><th>Project</th><th>Title</th><th>Opens</th></tr></thead>
              <tbody>
                {views.by_project.map((r) => (
                  <tr key={r.project_id} data-testid={`view-row-${r.project_id}`}>
                    <td>{r.project_id}</td>
                    <td>{r.title}</td>
                    <td className="num">{r.count}</td>
                  </tr>
                ))}
                {views.by_project.length === 0 && <tr><td colSpan={3} className="muted">No data yet.</td></tr>}
              </tbody>
            </table>
          </Panel>

          <Panel title="Engagement — read-time & scroll-depth" testid="panel-engagement">
            <table className="admin-table">
              <thead><tr><th>Project</th><th>Sessions</th><th>Avg Time</th><th>Avg Scroll Depth</th></tr></thead>
              <tbody>
                {engages.by_project.map((r) => (
                  <tr key={r.project_id} data-testid={`engage-row-${r.project_id}`}>
                    <td>{r.project_id}</td>
                    <td className="num">{r.sessions}</td>
                    <td className="num">{Math.round(r.avg_duration_ms / 1000)}s</td>
                    <td className="num">{Math.round(r.avg_scroll_depth)}%</td>
                  </tr>
                ))}
                {engages.by_project.length === 0 && <tr><td colSpan={4} className="muted">No sessions tracked yet.</td></tr>}
              </tbody>
            </table>
          </Panel>

          <Panel title="Contact Briefs" testid="panel-contacts">
            <table className="admin-table">
              <thead><tr><th>When</th><th>Name</th><th>Email</th><th>Project</th><th>Message</th></tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                    <td>{new Date(c.created_at).toLocaleString()}</td>
                    <td>{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.project}</td>
                    <td className="wrap">{c.message}</td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan={5} className="muted">No briefs yet.</td></tr>}
              </tbody>
            </table>
          </Panel>

          <Panel title="Callback Requests" testid="panel-callbacks">
            <table className="admin-table">
              <thead><tr><th>When</th><th>Name</th><th>Phone</th><th>Slot</th><th>Note</th></tr></thead>
              <tbody>
                {callbacks.map((c) => (
                  <tr key={c.id} data-testid={`callback-row-${c.id}`}>
                    <td>{new Date(c.created_at).toLocaleString()}</td>
                    <td>{c.name}</td>
                    <td className="num">{c.phone}</td>
                    <td>{c.slot}</td>
                    <td className="wrap">{c.note}</td>
                  </tr>
                ))}
                {callbacks.length === 0 && <tr><td colSpan={5} className="muted">No callbacks yet.</td></tr>}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}

function Panel({ title, testid, children }) {
  return (
    <section className="admin__panel" data-testid={testid}>
      <h2 className="serif italic">{title}</h2>
      <div className="admin__panel-body">{children}</div>
    </section>
  );
}
